import React, { useEffect, useState, ChangeEvent } from 'react';
import { Box, FormGroup, InputAdornment, IconButton, makeStyles, Theme, createStyles } from '@material-ui/core';
import { PasslyButton, ModalActionsComponent, PasslyTextField } from '@app/components';
import { Publish, Clear } from '@material-ui/icons';
import { useTypedTranslation } from '@app/hooks';
import { ISigningDialogState } from '@app/models';
import { generateCertificateFormData } from '@app/helpers';

interface IProps {
    onClose: () => void;
    onSubmit: (data: ISigningDialogState) => void;
    errorMessage?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        inputs: {
            'MuiFormControl-root': {
                marginBottom: theme.spacing(2)
            }
        },
        clearButton: {
            position: 'absolute',
            top: -theme.spacing(1),
            marginLeft: theme.spacing(1)
        }
    })
);

export const SigningDialog: React.SFC<IProps> = ({ onSubmit, onClose, errorMessage }: IProps) => {
    const classes = useStyles();
    const { translation } = useTypedTranslation();

    const initialState: ISigningDialogState = {
        file: null,
        fileInputValue: '',
        password: {
            value: '',
            show: false
        },
        subjectName: '',
        errorMessage: errorMessage
    };

    const [state, setState] = useState<ISigningDialogState>(initialState);

    useEffect(() => {
        setState(prevState => ({...prevState, errorMessage: errorMessage}));
    }, [errorMessage]);

    const closeModal = () => {
        resetState();
        onClose();
    };

    const submitModal = () => {
        onSubmit(state);
        resetState();
    };

    const resetState = () => {
        setState(initialState);
    };

    const resetFileInput = () => {
        setState({ ...state, file: initialState.file, fileInputValue: initialState.fileInputValue });
    };

    const updateState = (name: string, value: any) => {
        setState(prevState => ({ ...prevState, errorMessage: '' , [name]: value}));
    };

    const togglePasswordShow = () => {
        updateState('password', { ...state.password, show: !state.password.show });
    };

    const updatingSubject = () => {
        return Boolean(state.subjectName?.length);
    };

    const updatingFile = () => {
        return Boolean(state.file && state.password?.value.length);
    };

    const canSubmit = () => {
        return updatingFile() || updatingSubject();
    };

    // UPLOAD
    const setFiles = ({ target: { files, value } }: ChangeEvent<HTMLInputElement>) => {
        setState({ ...state, file: generateCertificateFormData(files as FileList), fileInputValue: value });
    };

    return (
        <>
            <Box p={2} className='signing-encryption-modal'>
                <p className='new-cert-help'>{translation('CHANGE_SIGNING_CERT_INSTRUCTIONS')}</p>
                <h4 className='signing-encryption__label'>{translation('UPLOAD_NEW_CERTIFICATE')}</h4>
                <Box mb={2}>
                    <PasslyButton variant='contained' color='primary' component='label' disabled={updatingSubject()} startIcon={<Publish />}>
                        <input className='image-uploader__upload-input' type='file' name='signingUpload' id='signingUpload-input' accept='.pfx' value={state.fileInputValue} onChange={setFiles} />
                        {translation('UPLOAD_NEW_SIGNING_CERTIFICATE')}
                    </PasslyButton>
                    {state.fileInputValue && (
                        <IconButton className={classes.clearButton} disableRipple onClick={() => resetFileInput()}>
                            <Clear />
                        </IconButton>
                    )}
                </Box>
                <Box mb={2} width={1 / 2}>
                    <FormGroup className={classes.inputs}>
                        <div className='form__row'>
                            <PasslyTextField
                                fullWidth
                                label={translation('SIGNING_KEY_PASSWORD')}
                                autoComplete='adminPassword-input'
                                value={state.password.value}
                                onChange={({ target: { name, value } }) => updateState(name, { ...state.password, value })}
                                id='certificatePassword-input'
                                name='password'
                                type={state.password.show ? 'text' : 'password'}
                                required
                                disabled={updatingSubject()}
                                autoFocus
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            <IconButton size='small' onClick={togglePasswordShow}>
                                                {state.password.show ? <i className='fa-regular fa-eye-slash' /> : <i className='fa-regular fa-eye' style={{width: '22.5px'}} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                        <h4 className='signing-encryption__label'>{translation('GENERATE_NEW_CERTIFICATE')}</h4>
                        <div className='form__row'>
                            <PasslyTextField
                                fullWidth
                                id='certificateSubjectName-input'
                                label={'SIGNING_KEY_SUBJECT'}
                                name='subjectName'
                                disabled={updatingFile()}
                                value={state.subjectName}
                                onChange={({ target: { name, value } }) => updateState(name, value)}
                                error={!!state.errorMessage}
                                helperText={state.errorMessage && state.errorMessage}
                            />
                        </div>
                    </FormGroup>
                </Box>
            </Box>
            <ModalActionsComponent
                primaryBtn={translation('UPDATE_CERTIFICATE')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={() => submitModal()}
                onClickSecondary={() => closeModal()}
                disabled={!canSubmit()}
                id='updateCertificate'
            />
        </>
    );
};
